/* eslint-disable @typescript-eslint/no-use-before-define */ // We must avoid a circular dependency with @react-aria/utils, and this useLayoutEffect is
// guarded by a check that it only runs on the client side.
import React, { useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';
// Default context value to use in case there is no SSRProvider. This is fine for
// client-only apps. In order to support multiple copies of React Aria potentially
// being on the page at once, the prefix is set to a random number. SSRProvider
// will reset this to zero for consistency between server and client, so in the
// SSR case multiple copies of React Aria is not supported.
const defaultContext = {
    prefix: String(Math.round(Math.random() * 10000000000)),
    current: 0
};
const SSRContext = /*#__PURE__*/ React.createContext(defaultContext);
const IsSSRContext = /*#__PURE__*/ React.createContext(false);
// This is only used in React < 18.
function LegacySSRProvider(props) {
    const cur = useContext(SSRContext);
    const counter = useCounter(cur === defaultContext);
    const [isSSR, setIsSSR] = useState(true);
    const value = useMemo(()=>({
            // If this is the first SSRProvider, start with an empty string prefix, otherwise
            // append and increment the counter.
            prefix: cur === defaultContext ? '' : `${cur.prefix}-${counter}`,
            current: 0
        }), [
        cur,
        counter
    ]);
    // If on the client, and the component was initially server rendered,
    // then schedule a layout effect to update the component after hydration.
    if (typeof document !== 'undefined') {
        // This if statement technically breaks the rules of hooks, but is safe
        // because the condition never changes after mounting.
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useLayoutEffect(()=>{
            setIsSSR(false);
        }, []);
    }
    return /*#__PURE__*/ React.createElement(SSRContext.Provider, {
        value: value
    }, /*#__PURE__*/ React.createElement(IsSSRContext.Provider, {
        value: isSSR
    }, props.children));
}
let warnedAboutSSRProvider = false;
/**
 * When using SSR with React Aria in React 16 or 17, applications must be wrapped in an SSRProvider.
 * This ensures that auto generated ids are consistent between the client and server.
 */ export function SSRProvider(props) {
    if (typeof React.useId === 'function') {
        if (process.env.NODE_ENV !== 'production' && !warnedAboutSSRProvider) {
            // eslint-disable-next-line no-console
            console.warn('In React 18, SSRProvider is not necessary and is a noop. You can remove it from your app.');
            warnedAboutSSRProvider = true;
        }
        return /*#__PURE__*/ React.createElement(React.Fragment, null, props.children);
    }
    return /*#__PURE__*/ React.createElement(LegacySSRProvider, props);
}
const canUseDOM = Boolean(typeof window !== 'undefined' && window.document && window.document.createElement);
const componentIds = new WeakMap();
function useCounter(isDisabled = false) {
    const ctx = useContext(SSRContext);
    const ref = useRef(null);
    if (ref.current === null && !isDisabled) {
        var // @ts-expect-error - Property '__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED' does not exist on type 'typeof React'
        _React___SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED_ReactCurrentOwner, _React___SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED;
        // In strict mode, React renders components twice, and the ref will be reset to null on the second render.
        // This means our id counter will be incremented twice instead of once. This is a problem because on the
        // server, components are only rendered once and so ids generated on the server won't match the client.
        // In React 18, useId was introduced to solve this, but it is not available in older versions. So to solve this
        // we need to use some React internals to access the underlying Fiber instance, which is stable between renders.
        // This is exposed as ReactCurrentOwner in development, which is all we need since StrictMode only runs in development.
        // To ensure that we only increment the global counter once, we store the starting id for this component in
        // a weak map associated with the Fiber. On the second render, we reset the global counter to this value.
        // Since React runs the second render immediately after the first, this is safe.
        const currentOwner = (_React___SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED = React.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED) === null || _React___SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED === void 0 ? void 0 : (_React___SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED_ReactCurrentOwner = _React___SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED.ReactCurrentOwner) === null || _React___SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED_ReactCurrentOwner === void 0 ? void 0 : _React___SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED_ReactCurrentOwner.current;
        if (currentOwner) {
            const prevComponentValue = componentIds.get(currentOwner);
            if (prevComponentValue == null) {
                // On the first render, and first call to useId, store the id and state in our weak map.
                componentIds.set(currentOwner, {
                    id: ctx.current,
                    state: currentOwner.memoizedState
                });
            } else if (currentOwner.memoizedState !== prevComponentValue.state) {
                // On the second render, the memoizedState gets reset by React.
                // Reset the counter, and remove from the weak map so we don't
                // do this for subsequent useId calls.
                ctx.current = prevComponentValue.id;
                componentIds.delete(currentOwner);
            }
        }
        ref.current = ++ctx.current;
    }
    return ref.current;
}
function useLegacySSRSafeId(defaultId) {
    const ctx = useContext(SSRContext);
    // If we are rendering in a non-DOM environment, and there's no SSRProvider,
    // provide a warning to hint to the developer to add one.
    if (ctx === defaultContext && !canUseDOM) {
        if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.warn('When server rendering, you must wrap your application in an <SSRProvider> to ensure consistent ids are generated between the client and server.');
        }
    }
    const counter = useCounter(!!defaultId);
    const prefix = ctx === defaultContext && process.env.NODE_ENV === 'test' ? 'react-aria' : `react-aria${ctx.prefix}`;
    return defaultId || `${prefix}-${counter}`;
}
function useModernSSRSafeId(defaultId) {
    const id = React.useId();
    const [didSSR] = useState(useIsSSR());
    const prefix = didSSR || process.env.NODE_ENV === 'test' ? 'react-aria' : `react-aria${defaultContext.prefix}`;
    return defaultId || `${prefix}-${id}`;
}
// Use React.useId in React 18 if available, otherwise fall back to our old implementation.
/** @private */ export const useSSRSafeId = typeof React.useId === 'function' ? useModernSSRSafeId : useLegacySSRSafeId;
function getSnapshot() {
    return false;
}
function getServerSnapshot() {
    return true;
}
// @ts-expect-error - unused onStoreChange parameter
function subscribe(onStoreChange) {
    // noop
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return ()=>{};
}
/**
 * Returns whether the component is currently being server side rendered or
 * hydrated on the client. Can be used to delay browser-specific rendering
 * until after hydration.
 */ export function useIsSSR() {
    // In React 18, we can use useSyncExternalStore to detect if we're server rendering or hydrating.
    if (typeof React.useSyncExternalStore === 'function') {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        return React.useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
    }
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useContext(IsSSRContext);
}

//# sourceMappingURL=SSRProvider.js.map